import './clientModelSelector.css'
import { useEffect, useState } from 'react'
import { listTrainingVersions, trainingDate, type TrainingVersion } from '../training/trainingApi'

export type ClientModelAnalytic = {
  technicalName: string
  displayName: string
  kind: 'object' | 'pose' | 'combined' | 'temporal'
  ready: boolean
  unavailableReason?: string | null
}

export type ClientModelOption = {
  id: string | null
  displayName: string
  versionLabel: string
  ready: boolean
  unavailableReason?: string | null
  analytics: ClientModelAnalytic[]
}

const unavailableReasons: Record<string, string> = {
  model_validation_required: 'O treinamento, a validação e a integração deste modelo ainda não foram concluídos.',
  pose_runtime_required: 'O modelo de postura ainda precisa ser integrado e validado antes do uso.',
  risk_rule_validation_required: 'A ameaça armada e os comportamentos de risco, como contenção por trás, ainda precisam de calibração e validação.',
  analytic_runtime_binding_required: 'Este analítico ainda não foi associado ao pacote validado do modelo.',
  temporal_behavior_labels_required: 'Este comportamento precisa de clipes temporais consentidos, negativos e validação própria antes do uso.',
  agent_upgrade_required: 'O agente de inteligência artificial precisa ser atualizado antes de usar esta opção.',
  model_artifact_unavailable: 'O arquivo validado deste modelo ainda não está disponível para execução.',
  behavior_alerts_disabled: 'Os alertas comportamentais ainda não foram liberados para uso em produção.',
}

export function clientModelUnavailableReason(reason?: string | null) {
  return reason ? unavailableReasons[reason] ?? 'Esta opção ainda não está disponível para uso.' : 'Esta opção ainda não está disponível para uso.'
}

type Props = {
  models: ClientModelOption[]
  selectedModelId: string | null
  loading: boolean
  error: string
  notice: string
  disabled: boolean
  onChange: (id: string | null) => void
}

export function ClientModelSelector({ models, selectedModelId, loading, error, notice, disabled, onChange }: Props) {
  const [versions, setVersions] = useState<TrainingVersion[]>([])
  useEffect(() => { listTrainingVersions().then(setVersions).catch(() => {}) }, [])
  const selectedVersion = versions.find(v => v.runtimeModelId === selectedModelId)
  const familyId = selectedVersion?.modelId ?? selectedModelId
  const familyVersions = versions.filter(v => v.modelId === familyId && models.some(m => m.id === v.runtimeModelId))
  const rootModels = models.filter(m => !versions.some(v => v.runtimeModelId === m.id && v.runtimeModelId !== v.modelId))
  const model = models.find(item => item.id === selectedModelId)
  return <section className="client-model-selector" aria-labelledby="client-model-title">
    <div className="client-model-step"><b aria-hidden="true">1</b><div><strong id="client-model-title">Escolha o modelo</strong><small>Depois, selecione somente os analíticos deste modelo.</small></div></div>
    <label htmlFor="client-ai-model">Modelo de inteligência artificial</label>
    <select id="client-ai-model" disabled={disabled || loading || !models.length} value={familyId ?? ''} onChange={event => onChange(event.target.value || null)}>
      {!models.length && <option value="">{loading ? 'Carregando modelos…' : 'Catálogo indisponível'}</option>}
      {rootModels.map(item => <option key={item.id ?? 'legacy'} value={item.id ?? ''} disabled={!item.ready && item.id !== selectedModelId}>{item.displayName}{item.ready ? '' : ' — Em preparação'}</option>)}
    </select>
    {!!familyVersions.length && <><label htmlFor="client-ai-version">Versão</label><select id="client-ai-version" disabled={disabled || loading} value={selectedModelId ?? ''} onChange={e => onChange(e.target.value)}>{familyVersions.map(v => <option key={v.runtimeModelId} value={v.runtimeModelId}>Versão {v.version}{v.status === 'awaiting_validation' ? ' · Aguardando avaliação' : ''}</option>)}</select><p>Treinamento: <strong>{trainingDate(selectedVersion?.trainedAtUtc ?? null)}</strong></p></>}
    {error && <p className="client-model-error" role="alert">{error}</p>}
    {model && <><div className="client-model-status"><span className={model.ready ? 'ready' : 'preparing'}>{model.ready ? 'Disponível para uso' : 'Em preparação'}</span><span>{model.id === null ? 'Operação atual' : `v${model.versionLabel}`}</span></div>
      {!model.ready && <p className="client-model-notice">{clientModelUnavailableReason(model.unavailableReason)}</p>}
      <div className="client-model-capabilities">{model.analytics.filter(item => ['person_clothing', 'car', 'carro', 'person', 'pessoa', 'hands_raised', 'knife', 'faca', 'arma_de_fogo', 'firearm', 'fire', 'smoke', 'license_plate', 'fallen_person', 'robbery_suspected', 'rear_restraint', 'blocking_cornering', 'persistent_approach_short_pursuit'].includes(item.technicalName)).map(item => <span key={item.technicalName} className={item.ready ? '' : 'preparing'} title={item.ready ? undefined : clientModelUnavailableReason(item.unavailableReason)}>{item.displayName}{!item.ready && <small>{clientModelUnavailableReason(item.unavailableReason)}</small>}</span>)}</div>
      <p className="client-model-hands-note">Mãos levantadas é uma opção independente: quando disponível e selecionada, gera seu próprio alerta, mesmo sem arma.</p>
      {model.analytics.some(item => item.technicalName === 'fallen_person' && item.ready) && <p className="client-model-hands-note">Para Possível pessoa caída, marque a área de chão de cada câmera em Mapeamento de região e escolha Detectar dentro. Camas e sofás devem ficar fora dessa área.</p>}
      <p className="client-model-hands-note">Os analíticos de contenção, bloqueio e acompanhamento avaliam sequências de movimento entre pessoas. Os alertas indicam suspeita para revisão.</p>
    </>}
    {notice && <p className="client-model-notice" role="status">{notice}</p>}
  </section>
}
